import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Building2,
  IndianRupee,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  ArrowLeft,
  Award,
  AlertTriangle,
  Activity as ActivityIcon,
  ChevronRight,
  Layers,
  Filter,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import KVSLoader from '../../../components/loaders/KVSLoader';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import img1 from '../../../assets/pmshribanner.webp';

import {
  UtilizationGauge,
  TrendLineChart,
  ExpenditureBarChart,
} from '../components/charts';

import { YearDropdown, PhaseDropdown } from '../components/filters';

import { useGetExpenditureActivityTreeQuery } from '../services/api';

import type {
  Subhead,
  Activity,
  HeadCode,
  MonthlyTrendData,
  ExpenditureType,
  ExpenditureActivityTreeNode,
} from '../types';
import { DEFAULT_HEADS, HEAD_COLORS } from '../types';

// Mock data generator
const generateMockSubheadAnalytics = (headCode: string, subheadId: string) => {
  const headInfo = DEFAULT_HEADS.find(h => h.code === headCode) || DEFAULT_HEADS[0];
  const sanctioned = 20000000 + Math.random() * 30000000;
  const received = sanctioned * (0.75 + Math.random() * 0.2);
  const utilization = 50 + Math.random() * 45;
  const spent = received * (utilization / 100);
  const targetUnits = 30 + Math.floor(Math.random() * 50);
  const completedUnits = Math.floor(targetUnits * (0.5 + Math.random() * 0.4));

  const subhead: Subhead = {
    id: parseInt(subheadId),
    headId: headInfo.id,
    headCode: headCode as HeadCode,
    headName: headInfo.name,
    code: `${headCode}.${subheadId}`,
    name: `Subhead ${subheadId} - ${['Infrastructure Development', 'Equipment & Furniture', 'Training & Capacity Building', 'Learning Materials'][parseInt(subheadId) % 4]}`,
    description: `Description for subhead ${subheadId} under ${headInfo.name}`,
    totalSanctioned: sanctioned,
    totalReceived: received,
    totalSpent: spent,
    totalUnspent: received - spent,
    utilizationPercentage: utilization,
    targetUnits,
    completedUnits,
    efficiencyPercentage: (completedUnits / targetUnits) * (spent / received) * 100,
    activityCount: 6,
  };

  const activities: Activity[] = Array(6).fill(null).map((_, idx) => {
    const actSanctioned = sanctioned / 6;
    const actReceived = actSanctioned * (0.75 + Math.random() * 0.2);
    const actUtilization = 40 + Math.random() * 55;
    const actTargetUnits = 5 + Math.floor(Math.random() * 15);
    const actCompletedUnits = Math.floor(actTargetUnits * (0.5 + Math.random() * 0.4));
    const expenditureType: ExpenditureType = idx % 2 === 0 ? 'recurring' : 'non-recurring';
    return {
      id: idx + 1,
      subheadId: parseInt(subheadId),
      subheadName: subhead.name,
      headId: headInfo.id,
      headName: headInfo.name,
      code: `${headCode}.${subheadId}.${idx + 1}`,
      name: [
        'Smart Classroom Setup',
        'Teacher Training Program',
        'Science Lab Equipment',
        'Sports Infrastructure',
        'Digital Learning Tools',
        'Library Enhancement',
      ][idx],
      description: `Activity ${idx + 1} description`,
      expenditureType,
      totalSanctioned: actSanctioned,
      totalReceived: actReceived,
      totalSpent: actReceived * (actUtilization / 100),
      totalUnspent: actReceived * (1 - actUtilization / 100),
      utilizationPercentage: actUtilization,
      targetUnits: actTargetUnits,
      completedUnits: actCompletedUnits,
      efficiencyPercentage: (actCompletedUnits / actTargetUnits) * (actUtilization / 100) * 100,
      subActivityCount: 3 + Math.floor(Math.random() * 5),
    };
  });

  const months = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
  const trends: MonthlyTrendData[] = months.map((month, idx) => {
    const monthSanctioned = (sanctioned / 12) * (0.8 + Math.random() * 0.4);
    const monthReceived = monthSanctioned * (0.75 + Math.random() * 0.2);
    const monthUtilization = 35 + (idx * 5) + Math.random() * 10;
    return {
      month,
      year: idx < 9 ? 2025 : 2026,
      sanctioned: monthSanctioned,
      received: monthReceived,
      spent: monthReceived * (monthUtilization / 100),
      utilization: monthUtilization,
      recurringSpent: monthReceived * 0.45 * (monthUtilization / 100),
      nonRecurringSpent: monthReceived * 0.55 * (monthUtilization / 100),
    };
  });

  return { subhead, activities, trends, headInfo };
};

type FilterType = 'all' | 'recurring' | 'non-recurring';

const SubheadAnalyticsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { headCode, subheadId } = useParams<{ headCode: string; subheadId: string }>();
  const carouselImages = [img1];

  // State
  const [filter, setFilter] = useState<FilterType>('all');
  const [sortBy, setSortBy] = useState<'name' | 'utilization' | 'sanctioned'>('name');
  const [selectedPhase, setSelectedPhase] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // Get current year for display (fallback to current fiscal year)
  const currentYear = selectedYear || new Date().getFullYear();

  // Fetch expenditure activity tree from API (provides sanTotal, spentTotal, sanRec, sanNonRec, spentRec, spentNonRec)
  const { data: expenseTreeData, isLoading, refetch } = useGetExpenditureActivityTreeQuery({
    ...(selectedYear !== null && { year: selectedYear }),
    ...(selectedPhase !== null && { phase: selectedPhase }),
  });

  // Find head and subhead from API data
  const { headFromAPI, subheadFromAPI } = useMemo(() => {
    if (!expenseTreeData?.success || !expenseTreeData?.data?.tree) {
      return { headFromAPI: null, subheadFromAPI: null };
    }
    
    const headNumber = parseInt(headCode?.replace('H', '') || '1');
    const headNode = expenseTreeData.data.tree.find((h: ExpenditureActivityTreeNode, idx: number) => h.id === headNumber || idx === headNumber - 1);
    
    if (!headNode) return { headFromAPI: null, subheadFromAPI: null };
    
    const subheadNode = headNode.children?.find((sh: ExpenditureActivityTreeNode) => sh.id === parseInt(subheadId || '0'));
    
    return { headFromAPI: headNode, subheadFromAPI: subheadNode };
  }, [expenseTreeData, headCode, subheadId]);

  // Get head info for color
  const headInfo = useMemo(() => {
    return DEFAULT_HEADS.find(h => h.code === headCode) || DEFAULT_HEADS[0];
  }, [headCode]);

  // Transform to Subhead type
  const subhead = useMemo<Subhead | null>(() => {
    if (!subheadFromAPI || !headFromAPI) return null;

    // Use actual values from Expenditure API
    const sanctioned = subheadFromAPI.sanTotal || 0;
    const spent = subheadFromAPI.spentTotal || 0;
    const utilization = sanctioned > 0 ? (spent / sanctioned) * 100 : 0;

    return {
      id: subheadFromAPI.id,
      headId: headFromAPI.id,
      headCode: headCode as HeadCode,
      headName: headFromAPI.title,
      code: `${headCode}.${subheadFromAPI.id}`,
      name: subheadFromAPI.title,
      description: subheadFromAPI.title,
      totalSanctioned: sanctioned,
      totalReceived: sanctioned, // Using sanctioned as received for this API
      totalSpent: spent,
      totalUnspent: sanctioned - spent,
      utilizationPercentage: utilization,
      targetUnits: 0,
      completedUnits: 0,
      efficiencyPercentage: 0,
      activityCount: subheadFromAPI.children?.length || 0,
    };
  }, [subheadFromAPI, headFromAPI, headCode]);

  // Transform to Activity array
  const activities = useMemo<Activity[]>(() => {
    if (!subheadFromAPI || !subheadFromAPI.children || !headFromAPI) return [];

    // All children of subhead are activities
    return subheadFromAPI.children
      .map((activity: ExpenditureActivityTreeNode, idx: number) => {
        // Use actual values from Expenditure API
        const sanctioned = activity.sanTotal || 0;
        const spent = activity.spentTotal || 0;
        const utilization = sanctioned > 0 ? (spent / sanctioned) * 100 : 0;
        // Determine expenditure type based on sanRec vs sanNonRec
        const expenditureType: ExpenditureType = (activity.sanRec || 0) > (activity.sanNonRec || 0) ? 'recurring' : 'non-recurring';

        return {
          id: activity.id,
          subheadId: subheadFromAPI.id,
          subheadName: subheadFromAPI.title,
          headId: headFromAPI.id,
          headName: headFromAPI.title,
          code: `${headCode}.${subheadFromAPI.id}.${activity.id}`,
          name: activity.title,
          description: activity.title,
          expenditureType,
          totalSanctioned: sanctioned,
          totalReceived: sanctioned, // Using sanctioned as received for this API
          totalSpent: spent,
          totalUnspent: sanctioned - spent,
          utilizationPercentage: utilization,
          targetUnits: 0,
          completedUnits: 0,
          efficiencyPercentage: 0,
          subActivityCount: activity.children?.length || 0,
        };
      });
  }, [subheadFromAPI, headFromAPI, headCode]);

  // Generate mock trends
  const trends = useMemo<MonthlyTrendData[]>(() => {
    const months = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
    const sanctioned = subhead?.totalSanctioned || 0;
    
    return months.map((month, idx) => {
      const monthSanctioned = (sanctioned / 12) * (0.8 + Math.random() * 0.4);
      const monthReceived = monthSanctioned * (0.75 + Math.random() * 0.2);
      const monthUtilization = 35 + (idx * 5) + Math.random() * 10;
      return {
        month,
        year: idx < 9 ? currentYear : currentYear + 1,
        sanctioned: monthSanctioned,
        received: monthReceived,
        spent: monthReceived * (monthUtilization / 100),
        utilization: monthUtilization,
        recurringSpent: monthReceived * 0.45 * (monthUtilization / 100),
        nonRecurringSpent: monthReceived * 0.55 * (monthUtilization / 100),
      };
    });
  }, [subhead, currentYear]);

  const formatCurrency = (value: number) => {
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  const filteredActivities = activities
    .filter(a => filter === 'all' ? true : a.expenditureType === filter)
    .sort((a, b) => {
      if (sortBy === 'utilization') return b.utilizationPercentage - a.utilizationPercentage;
      if (sortBy === 'sanctioned') return b.totalSanctioned - a.totalSanctioned;
      return a.name.localeCompare(b.name);
    });

  const recurringTotal = activities
    .filter(a => a.expenditureType === 'recurring')
    .reduce((acc, a) => acc + a.totalSpent, 0);
  const nonRecurringTotal = activities
    .filter(a => a.expenditureType === 'non-recurring')
    .reduce((acc, a) => acc + a.totalSpent, 0);

  const getStatusIcon = (utilization: number) => {
    if (utilization >= 75) return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (utilization >= 50) return <Clock className="w-5 h-5 text-yellow-500" />;
    return <XCircle className="w-5 h-5 text-red-500" />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center">
          <KVSLoader size={80} />
          <span className="mt-4 text-gray-600">Loading...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 pt-2 sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Page Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <button
                onClick={() => navigate(`/pm-shri/analytics/heads/${headCode}`)}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                  <span
                    className="px-2 py-0.5 rounded text-white text-xs font-bold"
                    style={{ backgroundColor: headInfo?.color }}
                  >
                    {headCode}
                  </span>
                  <ChevronRight className="w-4 h-4" />
                  <span>{subhead?.code}</span>
                </div>
                <h1 className="text-2xl font-bold text-gray-900">{subhead?.name}</h1>
                <p className="text-sm text-gray-500 mt-1">{subhead?.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Year Selector */}
              <YearDropdown
                value={selectedYear}
                onChange={setSelectedYear}
              />

              {/* Phase Selector */}
              <PhaseDropdown
                value={selectedPhase}
                onChange={setSelectedPhase}
              />

              <UtilizationGauge value={subhead?.utilizationPercentage || 0} size="md" label="Utilization" />
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <IndianRupee className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">Total Sanctioned</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(subhead?.totalSanctioned || 0)}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <span className="text-sm text-gray-600">Total Spent</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(subhead?.totalSpent || 0)}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span className="text-sm text-gray-600">Unspent</span>
            </div>
            <div className="text-2xl font-bold text-red-600">{formatCurrency(subhead?.totalUnspent || 0)}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <ActivityIcon className="w-5 h-5 text-purple-500" />
              <span className="text-sm text-gray-600">Activities</span>
            </div>
            <div className="text-2xl font-bold text-purple-600">{subhead?.activityCount}</div>
          </div>
        </div>

        {/* Expenditure Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Expenditure Type Breakdown</h3>
            <ExpenditureBarChart
              data={[
                {
                  name: 'This Subhead',
                  recurring: recurringTotal,
                  nonRecurring: nonRecurringTotal,
                  recurringUtilization: 65,
                  nonRecurringUtilization: 55,
                },
              ]}
              height={250}
              showUtilization={true}
            />
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Trend</h3>
            <TrendLineChart
              data={trends}
              height={250}
              showArea={false}
              dataKeys={['spent', 'utilization']}
            />
          </div>
        </div>

        {/* Activities Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ActivityIcon className="w-5 h-5" />
              Activities ({filteredActivities.length})
            </h3>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                {(['all', 'recurring', 'non-recurring'] as FilterType[]).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filter === f
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {f === 'all' ? 'All' : f === 'recurring' ? 'Recurring' : 'Non-Recurring'}
                  </button>
                ))}
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value="name">Sort by Name</option>
                <option value="utilization">Sort by Utilization</option>
                <option value="sanctioned">Sort by Amount</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredActivities.map((activity) => (
              <div
                key={activity.id}
                onClick={() => navigate(`/pm-shri/analytics/heads/${headCode}/subheads/${subheadId}/activities/${activity.id}`)}
                className="p-4 rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow-md cursor-pointer transition-all group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(activity.utilizationPercentage)}
                    <div>
                      <div className="font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">
                        {activity.name}
                      </div>
                      <div className="text-xs text-gray-500">{activity.code}</div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-purple-500" />
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    activity.expenditureType === 'recurring'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {activity.expenditureType === 'recurring' ? 'Recurring' : 'Non-Recurring'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {activity.subActivityCount} sub-activities
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div>
                    <div className="text-gray-500 text-xs">Sanctioned</div>
                    <div className="font-semibold">{formatCurrency(activity.totalSanctioned)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">Spent</div>
                    <div className="font-semibold text-green-600">{formatCurrency(activity.totalSpent)}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex-grow h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        activity.utilizationPercentage >= 75 ? 'bg-green-500' :
                        activity.utilizationPercentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(activity.utilizationPercentage, 100)}%` }}
                    />
                  </div>
                  <span className={`text-sm font-bold ${
                    activity.utilizationPercentage >= 75 ? 'text-green-600' :
                    activity.utilizationPercentage >= 50 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {activity.utilizationPercentage.toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SubheadAnalyticsScreen;
